import { SearchListingsQuery } from "@outschool/gql-frontend-generated";
import { OsCaptureContext } from "@outschool/platform";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { usePreviousValue } from "@outschool/ui-utils";
import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  DocumentNode,
  QueryHookOptions,
  QueryResult,
  OperationVariables,
} from "@outschool/ui-apollo";

export default function usePaginatedQuery<
  TVariables extends OperationVariables,
  TData,
  TReturn,
>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables>,
  getItems: (result: TData) => TReturn[],
  getResult: (
    previousResult: TData,
    fetchMoreResult: TData,
    items: TReturn[],
  ) => TData,
  getFetchMoreVariables: (result: QueryResult<TData>) => Partial<TVariables>,
  hasMoreItems: (data: TData) => boolean = (data: TData) =>
    getItems(data)?.length > 0,
): Omit<QueryResult<TData, TVariables>, "fetchMore"> & {
  hasMore: boolean;
  fetchMore: () => Promise<void>;
  isFetchingMore: boolean;
} {
  const [fetchMoreHasMore, setFetchMoreHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const firstRequestTimestamp = useRef(Date.now());
  const lastOffset = useRef<number | null>(options.variables?.offset);

  // reset hasMore when options/variables change
  const previousOptions = usePreviousValue(options);

  useEffect(() => {
    if (!_.isEqual(options, previousOptions)) {
      if (!fetchMoreHasMore) {
        setFetchMoreHasMore(true);
      }

      lastOffset.current = options.variables?.offset;
      firstRequestTimestamp.current = Date.now();
    }
  }, [options, previousOptions, fetchMoreHasMore]);

  const result = useQueryWithPreviousData<TData, TVariables>(query, options);
  const hasMore =
    fetchMoreHasMore &&
    // check if there were any results
    result.called &&
    !result.loading &&
    !!result.data &&
    hasMoreItems(result.data);

  const fetchMore = useCallback(async () => {
    if (isFetchingMore) {
      return;
    }
    setIsFetchingMore(true);
    try {
      const fetchMoreVariables = getFetchMoreVariables(result);

      await result.fetchMore({
        // @ts-ignore TS(2769): No overload matches this call.
        variables: { ...options.variables, ...fetchMoreVariables },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          // @ts-ignore TS(2345): Argument of type 'TData | undefined' is not assign... Remove this comment to see the full error message
          const items = getItems(fetchMoreResult);
          if (items?.length === 0) {
            // is it possible for hasMoreItems(fetchMoreResult) to be true here?
            setFetchMoreHasMore(false);
            return previousResult;
          } else {
            // check if we hit the edge of a page
            // @ts-ignore TS(2345): Argument of type 'TData | undefined' is not assign... Remove this comment to see the full error message
            setFetchMoreHasMore(hasMoreItems(fetchMoreResult));
            // @ts-ignore TS(2345): Argument of type 'TData | undefined' is not assign... Remove this comment to see the full error message

            const combinedResult = getResult(previousResult, fetchMoreResult, [
              ...getItems(previousResult),
              ...items,
            ]);

            if (
              result.data &&
              typeof result.data === "object" &&
              "searchListings" in result.data
            ) {
              const previousResultUids =
                (
                  previousResult as SearchListingsQuery
                )?.searchListings?.listings?.map(x => x.activity.uid) ?? [];
              const fetchMoreResultUids =
                (
                  fetchMoreResult as SearchListingsQuery
                )?.searchListings?.listings?.map(x => x.activity.uid) ?? [];
              const uniqueUids =
                (
                  combinedResult as SearchListingsQuery
                )?.searchListings?.listings?.map(x => x.activity.uid) ?? [];

              // log whenever we fetch listings that are already displayed
              if (
                fetchMoreResultUids.length &&
                uniqueUids.length <
                  previousResultUids.length + fetchMoreResultUids.length
              ) {
                const fetchMoreToPreviousResultIndexMapping =
                  fetchMoreResultUids.map(x => previousResultUids.indexOf(x));

                const messageOptions: OsCaptureContext = {
                  tags: {
                    package: "ui-components-website",
                    "ui-components-website.usePaginatedQuery.uniqueItemsLength":
                      uniqueUids.length,
                    "ui-components-website.usePaginatedQuery.previousResultLength":
                      previousResultUids.length,
                    "ui-components-website.usePaginatedQuery.fetchMoreResultLength":
                      fetchMoreResultUids.length,
                    "ui-components-website.usePaginatedQuery.searchUid":
                      options.variables?.searchUid,
                    "ui-components-website.usePaginatedQuery.anonymousId":
                      options.variables?.anonymousId,
                    "ui-components-website.usePaginatedQuery.query":
                      options.variables?.searchFilters?.q,
                    "ui-components-website.usePaginatedQuery.limit":
                      options.variables?.limit,
                    "ui-components-website.usePaginatedQuery.offset":
                      fetchMoreVariables?.offset,
                    "ui-components-website.usePaginatedQuery.cursor":
                      fetchMoreVariables?.cursor,
                    "ui-components-website.usePaginatedQuery.lastOffset":
                      lastOffset.current,
                    "ui-components-website.usePaginatedQuery.minutesSinceFirstRequest":
                      (Date.now() - firstRequestTimestamp.current) / 60000,
                    "ui-components-website.usePaginatedQuery.pageLocation":
                      options.variables?.pageLocation,
                  },
                  extra: {
                    fetchMoreToPreviousResultIndexMapping,
                  },
                };

                // This ideally shouldn't happen, but if we found zero new activities, we should set hasMore to false.
                // If we don't do this, we can end up in a loop of fetching more without ever displaying more.  Its
                // possible this scenario could be caused by promoted returning the incorrect page, for example.
                if (uniqueUids.length === previousResultUids.length) {
                  if (
                    fetchMoreVariables?.offset &&
                    fetchMoreVariables.offset < previousResultUids.length
                  ) {
                    OsPlatform.captureMessage(
                      "offset less than previousResult length",
                      messageOptions,
                    );
                  } else {
                    OsPlatform.captureError(
                      new Error(
                        "fetchMoreResult contains no new listings, creating fetchMore cycle",
                      ),
                      messageOptions,
                    );
                    setFetchMoreHasMore(false);
                  }
                } else {
                  OsPlatform.captureMessage(
                    "fetchMoreResult contains listings present in previousResult",
                    messageOptions,
                  );
                }
              }

              // update lastOffset
              lastOffset.current =
                fetchMoreVariables?.offset || lastOffset.current;
            }

            return combinedResult;
          }
        },
      });
    } catch (error) {
      OsPlatform.captureError(error, {
        tags: { package: "ui-components-website" },
      });
      setFetchMoreHasMore(false);
    } finally {
      setIsFetchingMore(false);
    }
  }, [
    getFetchMoreVariables,
    getItems,
    getResult,
    hasMoreItems,
    result,
    options.variables,
    isFetchingMore,
  ]);

  return {
    ...result,
    hasMore,
    fetchMore: hasMore ? fetchMore : async () => {},
    isFetchingMore,
  };
}
